import React, { useState } from "react";
import { ArrowUpRight, Phone, MessageSquare } from "lucide-react";

export function LetsWorkTogether() {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isCallHovered, setIsCallHovered] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    setIsClicked(true);

    setTimeout(() => {
      setShowSuccess(true);
    }, 500);

    // Reset back to normal after 2 seconds
    setTimeout(() => {
      setShowSuccess(false);
      // Wait for the success fade-out to finish before bringing back original content
      setTimeout(() => {
        setIsClicked(false);
        setIsHovered(false);
      }, 600);
    }, 2500);
  };

  const handleContact = () => {
    window.location.href = "/contact";
  };

  const handleCall = () => {
    window.location.href = "tel:+919363638811";
  };

  return (
    <div className="flex items-center justify-center px-6 py-16">
      <div className="relative flex flex-col items-center gap-12">
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-8 transition-all duration-700"
          style={{
            opacity: showSuccess ? 1 : 0,
            transform: showSuccess ? "translateY(0) scale(1)" : "translateY(20px) scale(0.95)",
            pointerEvents: showSuccess ? "auto" : "none",
          }}
        >
          {/* Elegant heading */}
          <div className="flex flex-col items-center gap-2">
            <span
              className="text-xs font-medium tracking-[0.3em] uppercase text-gray-400 transition-all duration-500"
              style={{
                transform: showSuccess ? "translateY(0)" : "translateY(10px)",
                opacity: showSuccess ? 1 : 0,
                transitionDelay: "100ms",
              }}
            >
              Perfect
            </span>
            <h3
              className="text-3xl font-light tracking-tight text-steel transition-all duration-500 sm:text-4xl"
              style={{
                transform: showSuccess ? "translateY(0)" : "translateY(10px)",
                opacity: showSuccess ? 1 : 0,
                transitionDelay: "200ms",
              }}
            >
              Let's connect
            </h3>
          </div>

          {/* Contact & Call buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Contact button */}
            <button
              onClick={handleContact}
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
              className="group relative flex items-center gap-4 transition-all duration-500 cursor-pointer"
              style={{
                transform: showSuccess
                  ? isButtonHovered
                    ? "translateY(0) scale(1.02)"
                    : "translateY(0) scale(1)"
                  : "translateY(15px) scale(1)",
                opacity: showSuccess ? 1 : 0,
                transitionDelay: "150ms",
              }}
            >
              <div
                className="relative flex items-center gap-3 overflow-hidden rounded-full border px-6 py-3 transition-all duration-500 sm:px-8 sm:py-4"
                style={{
                  borderColor: isButtonHovered ? "rgb(15,14,151)" : "#e5e7eb",
                  backgroundColor: isButtonHovered ? "rgb(15,14,151)" : "transparent",
                  boxShadow: isButtonHovered ? "0 0 30px rgba(15,14,151,0.15), 0 10px 40px rgba(15,14,151,0.1)" : "none",
                }}
              >
                <MessageSquare
                  className="w-4 h-4 sm:w-5 sm:h-5 transition-all duration-500"
                  strokeWidth={1.5}
                  style={{
                    color: isButtonHovered ? "#ffffff" : "#1a1a2e",
                  }}
                />
                <span
                  className="text-sm font-medium tracking-wide transition-all duration-500 sm:text-base"
                  style={{
                    color: isButtonHovered ? "#ffffff" : "#1a1a2e",
                  }}
                >
                  Contact Our Experts
                </span>
                <ArrowUpRight
                  className="w-4 h-4 sm:w-5 sm:h-5 transition-all duration-500"
                  strokeWidth={1.5}
                  style={{
                    color: isButtonHovered ? "#ffffff" : "#1a1a2e",
                    transform: isButtonHovered ? "translate(3px, -3px) scale(1.1)" : "translate(0, 0) scale(1)",
                  }}
                />
              </div>
            </button>

            {/* Call button */}
            <button
              onClick={handleCall}
              onMouseEnter={() => setIsCallHovered(true)}
              onMouseLeave={() => setIsCallHovered(false)}
              className="group relative flex items-center gap-4 transition-all duration-500 cursor-pointer"
              style={{
                transform: showSuccess
                  ? isCallHovered
                    ? "translateY(0) scale(1.02)"
                    : "translateY(0) scale(1)"
                  : "translateY(15px) scale(1)",
                opacity: showSuccess ? 1 : 0,
                transitionDelay: "300ms",
              }}
            >
              <div
                className="relative flex items-center gap-3 overflow-hidden rounded-full border px-6 py-3 transition-all duration-500 sm:px-8 sm:py-4"
                style={{
                  borderColor: isCallHovered ? "rgb(15,14,151)" : "#e5e7eb",
                  backgroundColor: isCallHovered ? "rgb(15,14,151)" : "transparent",
                  boxShadow: isCallHovered ? "0 0 30px rgba(15,14,151,0.15), 0 10px 40px rgba(15,14,151,0.1)" : "none",
                }}
              >
                <Phone
                  className="w-4 h-4 sm:w-5 sm:h-5 transition-all duration-500"
                  strokeWidth={1.5}
                  style={{
                    color: isCallHovered ? "#ffffff" : "#1a1a2e",
                  }}
                />
                <span
                  className="text-sm font-medium tracking-wide transition-all duration-500 sm:text-base"
                  style={{
                    color: isCallHovered ? "#ffffff" : "#1a1a2e",
                  }}
                >
                  Call: +91 93636 38811
                </span>
              </div>
            </button>
          </div>

          {/* Subtle subtext */}
          <span
            className="text-xs tracking-widest uppercase text-gray-400/60 transition-all duration-500"
            style={{
              transform: showSuccess ? "translateY(0)" : "translateY(10px)",
              opacity: showSuccess ? 1 : 0,
              transitionDelay: "450ms",
            }}
          >
            We respond within 2 hours
          </span>
        </div>

        <div
          className="flex items-center gap-3 transition-all duration-500"
          style={{
            opacity: isClicked ? 0 : 1,
            transform: isClicked ? "translateY(-20px)" : "translateY(0)",
            pointerEvents: isClicked ? "none" : "auto",
          }}
        >
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-sm font-medium tracking-widest uppercase text-gray-400">
            Available for projects
          </span>
        </div>

        <div
          className="group relative cursor-pointer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={(e) => handleClick(e)}
          style={{
            pointerEvents: isClicked ? "none" : "auto",
          }}
        >
          <div className="flex flex-col items-center gap-6">
            <h2
              className="relative text-center text-5xl font-light tracking-tight text-steel sm:text-6xl md:text-7xl lg:text-8xl transition-all duration-700"
              style={{
                opacity: isClicked ? 0 : 1,
                transform: isClicked ? "translateY(-40px) scale(0.95)" : "translateY(0) scale(1)",
              }}
            >
              <span className="block overflow-hidden">
                <span
                  className="block transition-transform duration-700"
                  style={{
                    transform: isHovered && !isClicked ? "translateY(-8%)" : "translateY(0)",
                  }}
                >
                  Let's work
                </span>
              </span>
              <span className="block overflow-hidden">
                <span
                  className="block transition-transform duration-700 delay-75"
                  style={{
                    transform: isHovered && !isClicked ? "translateY(-8%)" : "translateY(0)",
                  }}
                >
                  <span className="text-primary/60">together</span>
                </span>
              </span>
            </h2>

            <div className="relative mt-4 flex w-16 h-16 items-center justify-center sm:w-20 sm:h-20">
              <div
                className="pointer-events-none absolute inset-0 rounded-full border transition-all ease-out"
                style={{
                  borderColor: isClicked ? "rgb(15,14,151)" : isHovered ? "rgb(15,14,151)" : "#e5e7eb",
                  backgroundColor: isClicked ? "transparent" : isHovered ? "rgb(15,14,151)" : "transparent",
                  transform: isClicked ? "scale(3)" : isHovered ? "scale(1.1)" : "scale(1)",
                  opacity: isClicked ? 0 : 1,
                  transitionDuration: isClicked ? "700ms" : "500ms",
                }}
              />
              <ArrowUpRight
                className="w-6 h-6 sm:w-7 sm:h-7 transition-all"
                style={{
                  transform: isClicked
                    ? "translate(100px, -100px) scale(0.5)"
                    : isHovered
                      ? "translate(2px, -2px)"
                      : "translate(0, 0)",
                  opacity: isClicked ? 0 : 1,
                  color: isHovered && !isClicked ? "#ffffff" : "#1a1a2e",
                  transitionDuration: isClicked ? "600ms" : "500ms",
                }}
              />
            </div>
          </div>

          <div className="absolute -left-8 top-1/2 -translate-y-1/2 sm:-left-16">
            <div
              className="h-px w-8 bg-gray-200 transition-all duration-500 sm:w-12"
              style={{
                transform: isClicked ? "scaleX(0) translateX(-20px)" : isHovered ? "scaleX(1.5)" : "scaleX(1)",
                opacity: isClicked ? 0 : isHovered ? 1 : 0.5,
              }}
            />
          </div>
          <div className="absolute -right-8 top-1/2 -translate-y-1/2 sm:-right-16">
            <div
              className="h-px w-8 bg-gray-200 transition-all duration-500 sm:w-12"
              style={{
                transform: isClicked ? "scaleX(0) translateX(20px)" : isHovered ? "scaleX(1.5)" : "scaleX(1)",
                opacity: isClicked ? 0 : isHovered ? 1 : 0.5,
              }}
            />
          </div>
        </div>

        <div
          className="mt-8 flex flex-col items-center gap-4 text-center transition-all duration-500 delay-100"
          style={{
            opacity: isClicked ? 0 : 1,
            transform: isClicked ? "translateY(20px)" : "translateY(0)",
            pointerEvents: isClicked ? "none" : "auto",
          }}
        >
          <p className="max-w-md text-sm leading-relaxed text-gray-400">
            Still have questions about our laser cutting services? Let's create something exceptional together.
          </p>
          <span className="text-xs tracking-widest uppercase text-gray-400/60">alphalaserinfo@gmail.com</span>
        </div>
      </div>
    </div>
  );
}

export default LetsWorkTogether;
